import { useEffect, useState, useRef } from 'react';
import { X, Loader2, Box, Info } from 'lucide-react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    BackgroundVariant,
    ConnectionMode,
    type ReactFlowInstance,
    type Node,
    type Edge
} from 'reactflow';
import 'reactflow/dist/style.css';

import baseapi from '../../services/baseapi';
import {
    getAssetIcon,
    ServerSidebarContent,
    IloSidebarContent,
    CameraSidebarContent,
    SwitchSidebarContent,
    PDUSidebarContent
} from './DigitalTwinSidebar';

import SwitchNode from '../AssetTopology/nodes/SwitchNode';
import AssetNode from '../AssetTopology/nodes/AssetNode';
import CustomEdge from '../AssetTopology/CustomEdge';

interface AssetModalProps {
    open: boolean;
    assetId: number;
    assetName: string;
    assetType: string;
    assetIp: string;
    onClose: () => void;
}

const nodeTypes = {
    switchNode: SwitchNode,
    assetNode: AssetNode,
};

const edgeTypes = {
    custom: CustomEdge,
};

export default function AssetModal({
    open,
    assetId,
    assetName,
    assetType,
    assetIp,
    onClose,
}: AssetModalProps) {
    const { clientId } = useParams();
    const client_id = Number(clientId);
    const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loadingTopology, setLoadingTopology] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [topologySelectedAsset, setTopologySelectedAsset] = useState<{
        id: number;
        name: string;
        type: string;
        ip: string;
    } | null>(null);

    const Icon = getAssetIcon(assetType);
    const typeLabel = assetType.charAt(0).toUpperCase() + assetType.slice(1).replace(/_/g, ' ');

    useEffect(() => {
        if (open) {
            setTopologySelectedAsset(null);
            setIsSidebarOpen(true);
        }
    }, [open, assetId]);

    useEffect(() => {
        if (!open || assetType !== 'switch') return;

        const fetchSwitchTopology = async () => {
            setLoadingTopology(true);
            try {
                // Fetch all assets for reference mapping
                const assetRes = await baseapi.get('/assets', {
                    params: {
                        query: JSON.stringify(client_id ? { client_id } : {}),
                        skip: 0,
                        limit: 1000,
                        connection_check: true,
                    },
                });
                let allAssets: any[] = [];
                if (assetRes.data.status) {
                    allAssets = assetRes.data.data || [];
                }

                // Fetch interfaces for this specific switch
                const switchRes = await baseapi.get(`/interfaces/switch/${assetId}`, {
                    params: { client_id: client_id || null }
                });

                if (switchRes.data.status && switchRes.data.data) {
                    const interfaceData = switchRes.data.data.interfaces || [];
                    // const switchDetails = switchRes.data.data.switch || {
                    //     name: assetName,
                    //     id: assetId,
                    // };

                    const connections: any[] = [];
                    const seenAssetIds = new Set<number>();

                    interfaceData.forEach((inf: any) => {
                        let assetsInPort: any[] = [];
                        if (inf.connected_assets) {
                            try {
                                const parsed = typeof inf.connected_assets === 'string'
                                    ? JSON.parse(inf.connected_assets)
                                    : inf.connected_assets;
                                if (Array.isArray(parsed)) assetsInPort = parsed;
                            } catch { /* ignore */ }
                        }

                        if (assetsInPort.length === 0 && (inf.connected_asset || inf.connected_asset_id)) {
                            const asset = inf.connected_asset || allAssets.find((a: any) => a.id === inf.connected_asset_id);
                            if (asset) {
                                assetsInPort = [{
                                    asset_id: asset.id,
                                    asset_name: asset.name || asset.asset_name,
                                    asset_type: asset.type || asset.asset_type,
                                    asset_ip: asset.ip_address || asset.asset_ip,
                                    asset_status: asset.status,
                                    is_uplink: (asset._is_uplink ?? false) || (inf.is_promoted && (asset.type === 'switch' || asset.asset_type === 'switch')),
                                    connection_type: asset._connection_type ?? 'ethernet',
                                    has_problem: asset.has_problem
                                }];
                            }
                        }

                        assetsInPort.forEach((a: any) => {
                            const connAssetId = a.asset_id || a.id;
                            if (!connAssetId) return;
                            connections.push({
                                portId: inf.id,
                                assetId: connAssetId,
                                assetName: a.asset_name || a.name || 'Linked Asset',
                                assetType: a.asset_type || a.type || 'other',
                                assetIp: a.asset_ip || a.ip_address || 'Unknown',
                                assetStatus: a.asset_status || a.status,
                                isUplink: (a.is_uplink ?? false) || (inf.is_promoted && (a.asset_type === 'switch' || a.type === 'switch')),
                                connectionType: a.connection_type || 'ethernet',
                                hasProblem: a.has_problem ?? false
                            });
                        });
                    });

                    const uniqueAssetConnections = connections.filter((c: any) => {
                        if (seenAssetIds.has(c.assetId)) return false;
                        seenAssetIds.add(c.assetId);
                        return true;
                    });

                    const switchNodeId = `detail-switch-${assetId}`;
                    const isAP = (assetType as string) === 'access_point';
                    const centerPos = { x: 0, y: 0 };

                    const switchNode: Node = {
                        id: switchNodeId,
                        type: isAP ? 'assetNode' : 'switchNode',
                        position: centerPos,
                        data: {
                            switchName: assetName,
                            assetName: assetName,
                            assetId: assetId,
                            assetType: assetType,
                            assetIp: assetIp,
                            status: 'active',
                            hasProblem: false,
                            portMappings: interfaceData,
                            onDelete: () => { },
                            onPortClick: () => { },
                            readOnly: true,
                        },
                        draggable: false,
                    };

                    const detailNodes: Node[] = [switchNode];
                    const detailEdges: Edge[] = [];

                    const NODE_WIDTH = 220;
                    const MIN_RADIUS = 320;
                    const circumferenceRadius = (uniqueAssetConnections.length * NODE_WIDTH) / (2 * Math.PI);
                    const radius = Math.max(MIN_RADIUS, circumferenceRadius);
                    const angleStep = (2 * Math.PI) / (uniqueAssetConnections.length || 1);
                    const startAngle = -Math.PI / 2;

                    uniqueAssetConnections.forEach((conn: any, index: number) => {
                        const angle = startAngle + index * angleStep;
                        const assetPos = {
                            x: centerPos.x + radius * Math.cos(angle),
                            y: centerPos.y + radius * Math.sin(angle),
                        };

                        const assetNodeId = `detail-asset-${conn.assetId}`;
                        detailNodes.push({
                            id: assetNodeId,
                            type: 'assetNode',
                            position: assetPos,
                            data: {
                                assetId: conn.assetId,
                                assetName: conn.assetName,
                                assetType: conn.assetType,
                                assetIp: conn.assetIp,
                                status: conn.assetStatus,
                                hasProblem: conn.hasProblem,
                                readOnly: true,
                            },
                            draggable: false,
                        });

                        let apTargetHandle = 'top';
                        let apSourceHandle = 'bottom';

                        if (isAP) {
                            if (angle >= -Math.PI / 4 && angle < Math.PI / 4) {
                                apSourceHandle = 'right';
                                apTargetHandle = 'right-target';
                            } else if (angle >= Math.PI / 4 && angle < 3 * Math.PI / 4) {
                                apSourceHandle = 'bottom';
                                apTargetHandle = 'bottom-target';
                            } else if (angle >= 3 * Math.PI / 4 && angle < 5 * Math.PI / 4) {
                                apSourceHandle = 'left-source';
                                apTargetHandle = 'left';
                            } else {
                                apSourceHandle = 'top-source';
                                apTargetHandle = 'top';
                            }
                        }

                        if (conn.isUplink) {
                            const upStyle = { stroke: '#10b981', strokeWidth: 2 };
                            detailEdges.push({
                                id: `detail-edge-${conn.assetId}-${assetId}-${conn.portId}`,
                                source: assetNodeId,
                                sourceHandle: 'bottom',
                                target: switchNodeId,
                                targetHandle: isAP ? apTargetHandle : `port-in-${conn.portId}`,
                                type: 'custom',
                                animated: true,
                                style: upStyle,
                                data: {
                                    connection_type: conn.connectionType,
                                    is_uplink: true,
                                    originalStyle: upStyle,
                                    hideLabel: isAP,
                                },
                            });
                        } else {
                            const downStyle = { stroke: '#3b82f6', strokeWidth: 2 };
                            detailEdges.push({
                                id: `detail-edge-${assetId}-${conn.assetId}-${conn.portId}`,
                                source: switchNodeId,
                                sourceHandle: isAP ? apSourceHandle : `port-${conn.portId}`,
                                target: assetNodeId,
                                targetHandle: 'top',
                                type: 'custom',
                                animated: true,
                                style: downStyle,
                                data: {
                                    connection_type: conn.connectionType,
                                    is_uplink: false,
                                    originalStyle: downStyle,
                                    hideLabel: isAP,
                                },
                            });
                        }
                    });

                    setNodes(detailNodes);
                    setEdges(detailEdges);

                    setTimeout(() => {
                        reactFlowInstanceRef.current?.fitView({ padding: 0.8, duration: 500 });
                    }, 150);
                }
            } catch (err) {
                console.error("Failed to load switch details inside modal", err);
            } finally {
                setLoadingTopology(false);
            }
        };

        fetchSwitchTopology();
    }, [open, assetId, assetType, client_id, assetName, assetIp]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fade-in">
            {/* Modal Box */}
            <div className="bg-[#0b0e14] border border-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden w-[80vw] h-[80vh] min-w-[800px] min-h-[500px]">

                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-800 flex-shrink-0 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shadow-lg ${assetType === 'camera' ? 'bg-orange-600' :
                            assetType === 'switch' ? 'bg-blue-600' :
                                ['server', 'servers', 'ilo'].includes(assetType) ? 'bg-indigo-600' :
                                    assetType === 'pdu' ? 'bg-yellow-600' : 'bg-slate-600'
                            }`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="truncate">
                            <h2 className="text-sm font-black text-white truncate leading-tight tracking-tight uppercase">
                                {assetName || assetIp || 'Selected Asset'}
                            </h2>
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-tight mt-1">
                                {typeLabel} • {assetIp || 'Internal'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-hidden flex min-h-0 bg-[#0b0e14]">
                    {assetType === 'switch' ? (
                        <div className="flex flex-1 h-full min-h-0">
                            {/* ReactFlow Topology Canvas (Left Column) */}
                            <div className="flex-1 h-full relative border-r border-gray-800 min-h-0">
                                {loadingTopology ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm z-50">
                                        <div className="flex flex-col items-center gap-3">
                                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">
                                                Loading Topology...
                                            </p>
                                        </div>
                                    </div>
                                ) : null}
                                <ReactFlow
                                    nodes={nodes}
                                    edges={edges}
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange}
                                    connectionRadius={30}
                                    connectionMode={ConnectionMode.Loose}
                                    onInit={(instance) => { reactFlowInstanceRef.current = instance; }}
                                    nodeTypes={nodeTypes}
                                    edgeTypes={edgeTypes}
                                    minZoom={0.2}
                                    maxZoom={2.0}
                                    fitView
                                    fitViewOptions={{ padding: 0.8 }}
                                    style={{ background: '#090b0f' }}
                                    proOptions={{ hideAttribution: true }}
                                    nodesDraggable={false}
                                    nodesConnectable={false}
                                    elementsSelectable={false}
                                    deleteKeyCode={null}
                                    onNodeClick={(_event, node) => {
                                        if (node.id === `detail-switch-${assetId}`) {
                                            setTopologySelectedAsset(null);
                                            setIsSidebarOpen(true);
                                        } else if (node.data && node.data.assetId) {
                                            setTopologySelectedAsset({
                                                id: node.data.assetId,
                                                name: node.data.assetName,
                                                type: node.data.assetType,
                                                ip: node.data.assetIp
                                            });
                                            setIsSidebarOpen(true);
                                        }
                                    }}
                                >
                                    <Background
                                        variant={BackgroundVariant.Dots}
                                        gap={24}
                                        size={1}
                                        color="#1f2937"
                                    />
                                    <Controls className="!bg-gray-800 !border-gray-700 !fill-gray-400" />
                                </ReactFlow>
                                {!isSidebarOpen && (
                                    <button
                                        onClick={() => setIsSidebarOpen(true)}
                                        className="absolute top-4 right-4 z-[60] px-3 py-2 flex items-center gap-1.5 bg-[#0d1017] border border-gray-800 rounded-lg text-xs font-bold text-white transition-all duration-300 hover:bg-orange-500/20 hover:border-orange-500/40 shadow-lg cursor-pointer"
                                        title="Show Details Sidebar"
                                    >
                                        <Info className="w-3.5 h-3.5 text-orange-500" />
                                        <span className="uppercase tracking-widest text-[9px]">Show Details</span>
                                    </button>
                                )}
                            </div>

                            {/* Switch details sidebar inside the modal (Right Column) */}
                            {isSidebarOpen && (
                                <div className="w-[380px] h-full flex flex-col bg-[#0d1017] border-l border-gray-800 overflow-y-auto p-4 custom-scroll space-y-4 flex-shrink-0 relative">
                                    {/* Sidebar Header with Close Button */}
                                    <div className="flex items-center justify-between pb-3 border-b border-gray-800 border-dashed">
                                        <div className="truncate">
                                            <h3 className="text-[10px] font-bold text-white uppercase tracking-widest truncate">
                                                {topologySelectedAsset ? topologySelectedAsset.name : 'Switch Details'}
                                            </h3>
                                            <p className="text-[8px] text-gray-500 font-mono mt-0.5 truncate uppercase">
                                                {topologySelectedAsset ? `${topologySelectedAsset.type} • ${topologySelectedAsset.ip || 'Internal'}` : 'Switch Specifications'}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setIsSidebarOpen(false)}
                                            className="p-1 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                                            title="Close Sidebar"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Sidebar Content */}
                                    <div className="flex-1 space-y-4">
                                        {topologySelectedAsset ? (
                                            <>
                                                {/* Back to Switch Details Link */}
                                                <button
                                                    onClick={() => setTopologySelectedAsset(null)}
                                                    className="w-full text-left text-[9px] font-bold text-orange-500 hover:text-orange-400 uppercase tracking-widest flex items-center gap-1 transition-colors pb-2"
                                                >
                                                    ← Back to Switch Details
                                                </button>

                                                {/* Render corresponding content based on selected type */}
                                                {['server', 'servers'].includes(topologySelectedAsset.type) && (
                                                    <ServerSidebarContent assetId={topologySelectedAsset.id} client_id={client_id} />
                                                )}
                                                {topologySelectedAsset.type === 'ilo' && (
                                                    <IloSidebarContent assetId={topologySelectedAsset.id} />
                                                )}
                                                {topologySelectedAsset.type === 'camera' && (
                                                    <CameraSidebarContent assetId={topologySelectedAsset.id} client_id={client_id} />
                                                )}
                                                {topologySelectedAsset.type === 'pdu' && (
                                                    <PDUSidebarContent />
                                                )}
                                                {!['server', 'servers', 'camera', 'pdu', 'ilo'].includes(topologySelectedAsset.type) && (
                                                    <div className="py-20 text-center space-y-4">
                                                        <Box className="w-12 h-12 text-gray-700 mx-auto" />
                                                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                                                            Detailed metrics unavailable for this asset type
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <SwitchSidebarContent assetId={assetId} />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Default Modal Details View (Non-switch assets) */
                        <div className="flex-1 overflow-y-auto p-6 custom-scroll bg-[#0b0e14]">
                            <div className="max-w-4xl mx-auto w-full space-y-6">
                                {['server', 'servers'].includes(assetType) && (
                                    <ServerSidebarContent assetId={assetId} client_id={client_id} />
                                )}
                                {assetType === 'ilo' && (
                                    <IloSidebarContent assetId={assetId} />
                                )}
                                {assetType === 'camera' && (
                                    <CameraSidebarContent assetId={assetId} client_id={client_id} />
                                )}
                                {assetType === 'pdu' && (
                                    <PDUSidebarContent />
                                )}
                                {!['server', 'servers', 'camera', 'pdu', 'ilo'].includes(assetType) && (
                                    <div className="py-20 text-center space-y-4">
                                        <Box className="w-12 h-12 text-gray-700 mx-auto" />
                                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                                            Detailed metrics unavailable for this asset type
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
